import {
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material'
import TermsOfUse from 'components/TermsOfUse'
import BoxWithSafeZone from 'components/BoxWithSafeZone'
import { NotificationIndicator } from 'components/Notifications'
import R4RIcon from 'components/R4RIcon'
import { FC, ReactNode, useMemo, useState, useEffect } from 'react'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { useUsersProfile } from 'services/profile/profile.hooks'
import { useTermsOfUse } from 'services/terms/terms.hooks'
import {
  CustomBottomNavigation,
  CustomHeaderNavigation,
  DashboardContent
} from './Dashboard.styled'
import Sidebar from './Sidebar'
import TopBar from './TopBar'
import { useIntl } from 'react-intl'
import { useUserRoles, isOwnerOnly } from 'services/rbac/useUserRoles'
import { OWNER_ALLOWED_ROUTES, isPathAllowed } from 'services/rbac/routes.const'

import Logo from 'static/images/Logo-R4R-Mark-Navy.png'
import VantageMigrationNoticeDialog from 'components/VantageMigrationNoticeDialog'
import { useVantageMigrationNoticeCheck } from 'services/contracts/hooks/useVantageMigrationNoticeCheck'

interface DashboardProps {
  children: ReactNode
  noHeader?: boolean
  disableGutters?: boolean
}

const Dashboard: FC<DashboardProps> = ({ children, noHeader, disableGutters }) => {
  const { userInitials } = useUsersProfile()
  const navigation = useNavigate()
  const { pathname } = useLocation()
  const { hasUserConsented, isLoading } = useTermsOfUse()
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const userRoles = useUserRoles()
  const isOnlyOwner = isOwnerOnly(userRoles)
  const {
    shouldShowVantageMigrationNotice,
    dismissVantageMigrationNotice
  } = useVantageMigrationNoticeCheck()
  const [showVantageMigrationDialog, setShowVantageMigrationDialog] = useState(false)

  useEffect(() => {
    if (shouldShowVantageMigrationNotice) {
      setShowVantageMigrationDialog(true)
    }
  }, [shouldShowVantageMigrationNotice])

  const handleCloseVantageMigrationDialog = () => {
    setShowVantageMigrationDialog(false)
    dismissVantageMigrationNotice()
  }

  const currentButton = useMemo(() => {
    const patterns = [
      '/additional-services/*',
      '/faults/*',
      '/help/*',
      '/common-room-reservations/*',
      '/discounts/*',
      '/board/*',
      '/user/*',
      '/',
      '/offer/*'
    ]

    for (const pattern of patterns) {
      const possibleMatch = matchPath(pattern, pathname)
      if (possibleMatch !== null) {
        const base = possibleMatch.pathnameBase
        // Help and reservations are Helpdesk tabs — highlight the HelpDesk item.
        return base === '/help' || base === '/common-room-reservations' ? '/faults' : base
      }
    }

    return null
  }, [pathname])

  if (isLoading) {
    return null
  }

  if (!hasUserConsented) {
    return <TermsOfUse />
  }

  if (isOnlyOwner && !isPathAllowed(pathname, OWNER_ALLOWED_ROUTES)) {
    navigation('/faults')
    return null
  }

  return (
    <BoxWithSafeZone>
      {!noHeader && (
        <CustomHeaderNavigation>
          <Box>
            <IconButton onClick={() => navigation('/user')}>
              <Avatar>{userInitials}</Avatar>
            </IconButton>
          </Box>
          <Box
            sx={{
              img: {
                height: '56px',
                width: 'auto'
              }
            }}>
            <img src={Logo} alt='Resi4Rent Logo' />
          </Box>
          <Box>
            <NotificationIndicator />
          </Box>
        </CustomHeaderNavigation>
      )}
      {isDesktop && <TopBar />}
      {isDesktop && <Sidebar isOnlyOwner={isOnlyOwner} />}
      <DashboardContent component='main' noHeader={noHeader} disableGutters={disableGutters}>
        {children}
      </DashboardContent>
      <CustomBottomNavigation>
        <BottomNavigation
          value={currentButton}
          onChange={(_, value) => {
            navigation(value)
          }}>
          <BottomNavigationAction
            value='/'
            icon={<R4RIcon name='home' size={24} />}
            label={formatMessage({
              id: 'discounts.panel',
              defaultMessage: 'Panel'
            })}
            showLabel
            sx={{
              display: isOnlyOwner ? 'none' : 'inline-flex'
            }}
          />
          <BottomNavigationAction
            value='/faults'
            icon={<R4RIcon name='constructTab' size={24} />}
            label={formatMessage({
              id: 'discounts.helpDesk',
              defaultMessage: 'HelpDesk'
            })}
            showLabel
          />
          <BottomNavigationAction
            value='/board'
            icon={<R4RIcon name='megaphoneTab' size={24} />}
            label={formatMessage({
              id: 'dashboard.announcements',
              defaultMessage: 'Komunikaty'
            })}
            showLabel
            sx={{
              display: isOnlyOwner ? 'none' : 'inline-flex'
            }}
          />
          <BottomNavigationAction
            value='/additional-services'
            icon={<R4RIcon name='documentTab' size={24} />}
            showLabel
            label={formatMessage({
              id: 'dashboard.services',
              defaultMessage: 'Umowy'
            })}
            sx={{
              display: isOnlyOwner ? 'none' : 'inline-flex'
            }}
          />
          <BottomNavigationAction
            value='/user'
            icon={<R4RIcon name='personTab' size={24} />}
            showLabel
            label={formatMessage({
              id: 'dashboard.profile',
              defaultMessage: 'Profil'
            })}
          />
        </BottomNavigation>
      </CustomBottomNavigation>
      <VantageMigrationNoticeDialog
        isOpen={showVantageMigrationDialog}
        onClose={handleCloseVantageMigrationDialog}
      />
    </BoxWithSafeZone>
  )
}

export default Dashboard
